/**
 * Item drop slot — replaces the old OotItemContextMenuButton.
 * Displays a gossip stone when empty; accepts dragged items.
 * Click when filled clears the slot. Click when empty opens item picker.
 */
import { DragEvent, useState, useRef, useEffect, useCallback } from 'react';
import GossipStone from '@/core/assets/oot/classic/OoT_Gossip_Stone_Model.png';
import GossipStoneWoth from '@/core/assets/oot/classic/OoT_Gossip_Stone_Woth_Model.png';
import GossipStoneBarren from '@/core/assets/oot/classic/OoT_Gossip_Stone_Barren_Model.png';
import { OOT_ITEMS, type OotItemData } from './data/oot-items-data';

interface ItemDropSlotProps {
  /** Current item (controlled mode) */
  item?: OotItemData | null;
  /** Called when item changes */
  onItemChange?: (item: OotItemData | null) => void;
  /** Show barren style gossip stone */
  barren?: boolean;
  /** Show woth style gossip stone */
  woth?: boolean;
  /** Size in pixels */
  size?: number;
}

export function ItemDropSlot({
  item: controlledItem,
  onItemChange,
  barren,
  woth,
  size = 30,
}: ItemDropSlotProps) {
  const [localItem, setLocalItem] = useState<OotItemData | null>(controlledItem ?? null);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const item = controlledItem !== undefined ? controlledItem : localItem;

  // Sync controlled prop
  useEffect(() => {
    if (controlledItem !== undefined) setLocalItem(controlledItem);
  }, [controlledItem]);

  const updateItem = useCallback((newItem: OotItemData | null) => {
    setLocalItem(newItem);
    onItemChange?.(newItem);
    setShowPicker(false);
    setSearch('');
  }, [onItemChange]);

  // Close picker on outside click
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  // Focus search when picker opens
  useEffect(() => {
    if (showPicker) inputRef.current?.focus();
  }, [showPicker]);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    try {
      const data: OotItemData = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.img && data.name) {
        updateItem(data);
      }
    } catch { /* ignore invalid drops */ }
  };

  const onDragOver = (e: DragEvent) => e.preventDefault();

  const handleClick = () => {
    if (item) {
      updateItem(null);
    } else {
      setShowPicker(true);
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateItem(null);
  };

  const gossipSrc = barren ? GossipStoneBarren : woth ? GossipStoneWoth : GossipStone;

  const filteredItems = search
    ? OOT_ITEMS.filter(
        (it) =>
          it.name.toLowerCase().includes(search.toLowerCase()) ||
          it.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()))
      )
    : OOT_ITEMS;

  return (
    <div className="relative inline-flex" onDrop={onDrop} onDragOver={onDragOver}>
      <div
        className="cursor-pointer hover:brightness-125 transition-all rounded"
        onClick={handleClick}
        onContextMenu={handleRightClick}
        title={item ? `${item.name} (click to clear)` : 'Drop item here'}
      >
        <img
          src={item ? item.img : gossipSrc}
          alt={item ? item.name : 'Gossip Stone'}
          width={size}
          height={size}
          draggable={false}
        />
      </div>

      {/* Item picker popup */}
      {showPicker && (
        <div
          ref={pickerRef}
          className="absolute z-50 top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-2 min-w-[280px]"
        >
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item..."
            className="w-full px-2 py-1 mb-2 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:border-zelda-gold focus:outline-none"
          />
          <div className="flex flex-wrap gap-1 max-h-[200px] overflow-y-auto">
            {filteredItems.map((it) => (
              <button
                key={it.name}
                onClick={() => updateItem(it)}
                className="p-0.5 rounded hover:bg-gray-600 transition-colors"
                title={it.name}
              >
                <img src={it.img} alt={it.name} width={32} height={32} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
